import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useId, type KeyboardEvent, type ReactNode } from "react";

export const TYPEFORM_TEXT_COLOR = "var(--color-ink)";

// Shaped like the caseblink-landing Button (4px radius, 44px, chevron
// affordance, accent focus outline) but filled with the app's brand blue.
const TYPEFORM_BUTTON_BASE =
  "h-11 rounded-xs text-base font-normal transition-colors duration-150 ease-[cubic-bezier(0.4,0,0.2,1)] active:not-aria-[haspopup]:translate-y-0 disabled:opacity-35 focus-visible:ring-0 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-solid focus-visible:outline-primary";
const TYPEFORM_PRIMARY_BUTTON = `${TYPEFORM_BUTTON_BASE} gap-2 px-5 bg-primary text-primary-foreground hover:bg-primary/90`;
const TYPEFORM_BACK_BUTTON = `${TYPEFORM_BUTTON_BASE} w-11 px-0 inset-ring-line-3 bg-elevated text-fg hover:bg-fill-1 hover:text-fg`;

export const StepBadge = ({ stepNumber }: { stepNumber: number }) => (
  <span className="mt-1.25 inline-flex h-4.5 min-w-4.5 shrink-0 items-center justify-center rounded-xs bg-primary text-sm font-medium text-primary-foreground">
    {stepNumber}
  </span>
);

/**
 * One numbered question: the badge, the prompt, the answer area and its OK.
 * Questions other than the active one blur, dim and hide their OK; focusing or
 * clicking into one makes it active.
 */
export const TypeformQuestion = ({
  stepNumber,
  title,
  description,
  actions,
  isActive = true,
  onActivate,
  sectionRef,
  children
}: {
  stepNumber: number;
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  isActive?: boolean;
  onActivate?: () => void;
  sectionRef?: (el: HTMLElement | null) => void;
  children: ReactNode;
}) => {
  const titleId = useId();
  return (
    <section
      ref={sectionRef}
      aria-labelledby={titleId}
      tabIndex={-1}
      onFocusCapture={onActivate}
      onMouseDown={onActivate}
      className={cn(
        "flex flex-col outline-none transition-[opacity,filter] duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] animate-in animation-duration-180 slide-in-from-bottom-3",
        isActive
          ? "opacity-100"
          : "opacity-35 blur-[3px] hover:opacity-60 hover:blur-[1.5px]"
      )}
    >
      <div className="flex items-start gap-3">
        <StepBadge stepNumber={stepNumber} />
        <p
          id={titleId}
          className="text-xl leading-tight font-medium text-fg md:text-2xl"
        >
          {title}
        </p>
      </div>
      {description ? (
        <p className="mt-3 text-lg text-fg md:ml-7.5">{description}</p>
      ) : null}
      <div className="md:ml-7.5">
        {children}
        {actions ? (
          // Hidden rather than unmounted so activating a question doesn't
          // shift everything below it.
          <div
            className={isActive ? "visible" : "invisible"}
            aria-hidden={!isActive || undefined}
          >
            {actions}
          </div>
        ) : null}
      </div>
    </section>
  );
};

export const TypeformActions = ({
  primaryLabel,
  onPrimary,
  isPrimaryDisabled = false,
  isPrimaryLoading = false,
  onBack,
  backLabel = "Previous question",
  hint = "press Enter ↵"
}: {
  primaryLabel: string;
  onPrimary: () => void;
  isPrimaryDisabled?: boolean;
  isPrimaryLoading?: boolean;
  onBack?: () => void;
  backLabel?: string;
  hint?: string;
}) => (
  <div
    className={cn(
      "mt-10 flex items-center gap-4",
      onBack ? "justify-between" : "justify-end"
    )}
  >
    {onBack ? (
      <Button
        type="button"
        variant="secondary"
        aria-label={backLabel}
        onClick={onBack}
        className={TYPEFORM_BACK_BUTTON}
      >
        <ChevronLeft className="size-3.5" strokeWidth={2.5} aria-hidden />
      </Button>
    ) : null}
    <div className="relative shrink-0">
      <Button
        variant="primary"
        type="button"
        onClick={onPrimary}
        disabled={isPrimaryDisabled || isPrimaryLoading}
        aria-busy={isPrimaryLoading || undefined}
        className={TYPEFORM_PRIMARY_BUTTON}
      >
        {primaryLabel}
        {isPrimaryLoading ? (
          <Spinner className="size-3.5" aria-hidden />
        ) : (
          <ChevronRight
            className="size-3.5 transition-transform duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover/button:translate-x-0.5"
            strokeWidth={2.5}
            aria-hidden
          />
        )}
      </Button>
      <span
        className={cn(
          "pointer-events-none absolute top-full left-1/2 mt-2 -translate-x-1/2 text-sm whitespace-nowrap text-accent-seed",
          !isPrimaryDisabled && !isPrimaryLoading ? "visible" : "invisible"
        )}
      >
        {hint}
      </span>
    </div>
  </div>
);

/**
 * Full-height typeform canvas: a progress bar pinned to the top and the
 * questions stacked in one column. The top pad starts question 1 near the
 * middle of the screen; the bottom pad lets the last one scroll up to it.
 */
export const TypeformShell = ({
  progress,
  onKeyDown,
  children
}: {
  progress: number;
  onKeyDown?: (event: KeyboardEvent<HTMLDivElement>) => void;
  children: ReactNode;
}) => (
  <div
    className="relative h-full min-h-[calc(100vh-46px)] w-full text-accent-seed"
    onKeyDown={onKeyDown}
  >
    <div className="sticky top-0 z-2 bg-surface/36 backdrop-blur-lg">
      <Progress
        value={progress}
        className="h-0.75 rounded-none bg-primary/12 [&>[data-slot=progress-indicator]]:duration-300 [&>[data-slot=progress-indicator]]:ease-[cubic-bezier(0.4,0,0.2,1)]"
      />
    </div>

    <div className="relative z-1 px-6 md:px-12">
      <div
        className="mx-auto flex w-full max-w-180 flex-col gap-16 px-5 md:gap-24 md:px-10"
        style={{ paddingTop: "28vh", paddingBottom: "50vh" }}
      >
        {children}
      </div>
    </div>
  </div>
);
